import { useCallback, useMemo, useState } from 'react'
import Modal from 'core/components/dialogs/Modal'
import { TransactionShort } from 'types/entity'
import { CameraIcon } from 'core/_legacy/icons'
import PdfViewer from 'core/widgets/pdf-viewer/PdfViewer'
import PDFCover from '../../../assets/images/pdf.png'
import cn from 'utils/cn'
import { Upload, UploadProps } from 'antd'
import Icon from 'core/components/icons/Icon'

interface Props {
  onChange?: (image: File | null) => void
  value?: TransactionShort['photo']
  disabled?: boolean
}

const FilePicker = ({ onChange, value, disabled = false }: Props) => {
  const [file, setFile] = useState<File | null>(null)
  const [show, setShow] = useState(false)
  const uploaderProps: UploadProps = {
    name: 'file',
    accept: 'image/*,.pdf',
    multiple: false,
    showUploadList: false,
    disabled,
    customRequest: () => {
      return null
    },
    beforeUpload: (file) => {
      setFile(file)
      onChange?.(file)
      return false
    }
  }

  const isPdf =
    file?.type.includes('pdf') ||
    value?.image_url?.includes('pdf') ||
    file?.name?.includes('pdf')
  const isImage =
    (file?.type.includes('image') || !value?.image_url?.includes('pdf')) &&
    !isPdf

  const handleRemoveFile = useCallback(() => {
    setFile(null)
    onChange?.(null)
  }, [onChange])

  const fileSrc = useMemo(() => {
    if ((isImage || isPdf) && file) return URL.createObjectURL(file)
    return value?.image_url
  }, [file, isImage, isPdf, value?.image_url])

  const fileContent = useMemo(() => {
    if (!file && !value?.token_id) return null

    return (
      <div
        className={cn([
          'h-36 rounded-md relative',
          isImage && 'w-36',
          isPdf && 'w-28'
        ])}
      >
        {isImage ? (
          <img
            src={fileSrc}
            className="rounded-md h-full w-full object-cover"
            alt="Transaction attachment"
          />
        ) : (
          <img
            src={PDFCover}
            alt="PDF attachment"
            className="rounded-md h-full bg-white"
          />
        )}

        <div
          className={cn([
            'absolute cursor-pointer gap-3 top-0 left-0 h-full w-full flex items-center justify-center opacity-0 hover:bg-dark hover:opacity-70 transition-opacity',
            isImage && 'rounded-md',
            isPdf && 'rounded-2xl rounded-tr-[40px]'
          ])}
        >
          <Icon
            name="eye-open"
            className="text-white hover:text-primary text-2xl"
            size={18}
            onClick={() => {
              setShow(true)
            }}
          />
          {!disabled && (
            <Icon
              name="trash-outline"
              className="text-white hover:text-danger text-2xl"
              size={18}
              onClick={handleRemoveFile}
            />
          )}
        </div>
      </div>
    )
  }, [
    disabled,
    file,
    fileSrc,
    handleRemoveFile,
    isImage,
    isPdf,
    value?.token_id
  ])

  const fileName = useMemo(() => {
    if (file) return file.name
    const arr = value?.image_url?.split('/') ?? []
    return arr[arr.length - 1]
  }, [file, value])

  return (
    <div className="pl-2">
      <div className="w-full">
        {!file && !value?.token_id ? (
          <div
            className={cn([
              'h-36 w-36 flex items-center rounded-md justify-center border  border-dashed',
              disabled
                ? 'border-transparent'
                : 'border-dark-2 hover:border-primary cursor-pointer'
            ])}
          >
            <Upload {...uploaderProps}>
              <div
                className={cn([
                  'flex justify-center flex-col items-center',
                  disabled && 'cursor-auto'
                ])}
              >
                <div className="relative w-fit">
                  <CameraIcon width={'45'} height={'55'} />
                  {!disabled && (
                    <span className="bottom-1 -left-1 flex items-center justify-center h-[15px] w-[15px] rounded-full bg-white text-primary absolute shadow-[0px_2px_8px] shadow-dark/20 text-lg">
                      +
                    </span>
                  )}
                </div>

                <p className="text-dark-3">
                  {disabled ? 'No receipt' : 'Add receipt'}
                </p>
              </div>
            </Upload>
          </div>
        ) : (
          fileContent
        )}
      </div>

      <Modal
        show={show}
        onClose={() => {
          setShow(false)
        }}
        className="-left-[7rem]  md:-left-[10rem] max-h-[50vh]"
        backdropClassName="z-50!"
        title={fileName}
      >
        <div className="overflow-hidden">
          {isImage ? (
            <img src={fileSrc} className="rounded-2xl" alt="Expense detail" />
          ) : (
            <PdfViewer
              src={value?.image_url ?? (file as File)}
              className="h-[50vh]"
            />
          )}
        </div>
      </Modal>
    </div>
  )
}

export default FilePicker
